import { getActivePinia } from 'pinia';

import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';

import authService from '@/modules/auth/services/authService';
import { useAuthStore } from '@/modules/auth/store/authStore';

const getAuthStore = () => {
	const pinia = getActivePinia();
	if (pinia) {
		return useAuthStore(pinia);
	}
	return null;
};

export default {
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	request: <T = any>({
		url,
		method,
		data = {},
		params = {},
		headers = {},
		responseType,
		signal,
		baseURL,
		paramsSerializer,
	}: AxiosRequestConfig & { baseURL?: string }): Promise<
		// biome-ignore lint/suspicious/noExplicitAny: Init Biome
		AxiosResponse<T, any>
	> => {
		const config: AxiosRequestConfig = {
			url,
			method,
			data,
			params,
			responseType,
			signal,
			paramsSerializer,
		};

		if (baseURL !== undefined) config.baseURL = baseURL;

		const tokens = authService.getTokens();
		if (tokens) {
			// biome-ignore lint/complexity/useLiteralKeys: Init Biome
			headers['Authorization'] = `Bearer ${tokens.access_token}`;
		}

		const authStore = getAuthStore();

		// Use Pinia auth store for supervisor agency ID
		const supervisorAgencyId = authStore?.supervisorAgencyId;
		if (supervisorAgencyId) {
			// biome-ignore lint/complexity/useLiteralKeys: Init Biome
			headers['SUPERVISOR_AGENCY_ID'] = supervisorAgencyId;
		}

		// Send selected agency ID for all users (multi-agency support)
		const currentAgencyId = authStore?.currentAgency?.id;
		if (currentAgencyId) {
			headers['X-AGENCY-ID'] = currentAgencyId.toString();
		}

		// Read selectedCollaborators/selectedDomains from Pinia auth store
		if (authStore?.selectedCollaborators?.length) {
			// biome-ignore lint/complexity/useLiteralKeys: Init Biome
			headers['COLLABORATORS_SELECTION'] = authStore.selectedCollaborators;
		}

		if (authStore?.selectedDomains?.length) {
			// biome-ignore lint/complexity/useLiteralKeys: Init Biome
			headers['BUSINESSSECTORS_SELECTION'] = authStore.selectedDomains;
		}

		// x-user-id: prefer the Pinia user; fall back to the JWT `sub` claim so
		// the header is always sent when a token exists (Pinia may not be
		// populated yet — e.g. early calls from Layout init before waitUser
		// resolves, or from router guards).
		let currentUserId = authStore?.currentUser?.id;
		if (!currentUserId && tokens?.access_token) {
			try {
				currentUserId = authService.getUserInfoFromToken(
					tokens.access_token
				).id;
			} catch {
				// malformed token — let the backend reject with 401
			}
		}
		if (currentUserId) {
			headers['x-user-id'] = currentUserId.toString();
		}

		// biome-ignore lint/suspicious/noAssignInExpressions: Init Biome
		headers && (config.headers = headers);

		return axios<T>(config);
	},
};
